import { useOutletContext } from "react-router";
import { styled } from "@pigment-css/react";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import { Message, Page, PageLoadingSkeleton } from "@acto/taskpane/field-sales-2/components/patterns/PageLayout";
import type { CustomerOutletContext } from "@acto/taskpane/field-sales-2/CustomerDetailsLayout";

const TitleBlock = styled("section")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[4],
  // 16px horizontal inset matches the all-todos overview card float (and the List below).
  padding: `0 ${theme.fieldSalesTwo.spacing[16]} ${theme.fieldSalesTwo.spacing[8]}`,
}));

const Heading = styled("h1")(({ theme }) => ({
  margin: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[22],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[120],
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.subtle,
  color: theme.fieldSalesTwo.colors.neutral[900],
}));

const HeadingSub = styled("span")(({ theme }) => ({
  display: "block",
  color: theme.fieldSalesTwo.colors.neutral[800],
}));

const List = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[12],
  // 16px horizontal inset so the pill rows float at the same edge as the all-todos overview cards.
  padding: `${theme.fieldSalesTwo.spacing[12]} ${theme.fieldSalesTwo.spacing[16]} ${theme.fieldSalesTwo.spacing[32]}`,
}));

const Row = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: theme.fieldSalesTwo.spacing[6],
  padding: `${theme.fieldSalesTwo.spacing[12]} ${theme.fieldSalesTwo.spacing[24]}`,
  borderRadius: theme.fieldSalesTwo.radius[24],
  background: theme.fieldSalesTwo.colors.surface.glass,
  border: `${theme.strokeWidth.thin} solid ${theme.fieldSalesTwo.colors.surface.glassBorder}`,
}));

const Label = styled("span")(({ theme }) => ({
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[120],
  color: theme.fieldSalesTwo.colors.neutral[800],
  overflowWrap: "anywhere",
}));

const Value = styled("span")(({ theme }) => ({
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[120],
  color: theme.fieldSalesTwo.colors.neutral[900],
  overflowWrap: "anywhere",
}));

export function CustomerInfo() {
  const t = useTranslations("fieldSalesTwo.customerInfo");
  const tCommon = useTranslations("fieldSalesTwo");
  const { locale } = useLocale();
  const { customer, isPending } = useOutletContext<CustomerOutletContext>();

  if (!customer) {
    return (
      <Page aria-busy={isPending || undefined}>
        {isPending ? (
          <PageLoadingSkeleton />
        ) : (
          <Message>{tCommon("noData", { defaultValue: "No data available" })}</Message>
        )}
      </Page>
    );
  }

  const headingTop = customer.street;
  const headingBottom = [customer.postcode, customer.city].filter(Boolean).join(" ");

  const entries: { key: string; label: string; value: string }[] = [
    { key: "customerNumber", label: t("customerNumber"), value: customer.externalReference },
  ];

  if (customer.industry) entries.push({ key: "category", label: t("category"), value: customer.industry });
  if (customer.region) entries.push({ key: "region", label: t("region"), value: customer.region });

  for (const tag of customer.clientTags ?? []) {
    if (tag.name.hasTranslations() && tag.value) {
      entries.push({ key: `tag-${tag.key}`, label: tag.name.getTranslation(locale), value: tag.value });
    }
  }

  return (
    <Page>
      <TitleBlock>
        <Heading>
          {headingTop}
          {headingBottom && <HeadingSub>{headingBottom}</HeadingSub>}
        </Heading>
      </TitleBlock>
      <List>
        {entries.map((entry) => (
          <Row key={entry.key}>
            <Label>{entry.label}</Label>
            <Value>{entry.value}</Value>
          </Row>
        ))}
      </List>
    </Page>
  );
}
